import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  CancelAllFileUploadJobs,
  DATA_SELECTOR_INJECTION_TOKEN,
  DmsIntegrationStateSelectors,
  FileSizePipe,
  ResetUploadToast,
  SetUploadToastExpanded,
  SharedModule,
} from '@aiwyn/shared';
// The per-file row is reused as-is (compact mode = the Figma upload row). It
// self-binds to the DMS store by jobId and owns its own cancel/retry.
import { FileUploadJobItemComponent } from '../../../../client-requests/containers/client-request-details/client-request-panel/components/file-upload-job-item/file-upload-job-item.component';

/**
 * Global upload-progress toast (bottom-right; bottom-center on mobile). Mounted
 * once in the app shell so it shows on any route. It is purely a projection of
 * the DMS upload state: every count / byte total / percent / phase comes from
 * `DmsIntegrationStateSelectors.getUploadToastSummary` (computed over the tracked
 * job set), and it dispatches the expand / cancel-all / dismiss actions. The only
 * component logic here is label formatting.
 */
@Component({
  selector: 'cxp-upload-toast',
  standalone: true,
  imports: [SharedModule, MatProgressBarModule, FileSizePipe, FileUploadJobItemComponent],
  templateUrl: './upload-toast.component.html',
  styleUrls: ['./upload-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: DATA_SELECTOR_INJECTION_TOKEN, useValue: 'cxp-upload-toast' }],
})
export class UploadToastComponent {
  private readonly store = inject(Store);
  protected readonly dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  protected readonly summary = this.store.selectSignal(DmsIntegrationStateSelectors.getUploadToastSummary);
  protected readonly jobIds = this.store.selectSignal(DmsIntegrationStateSelectors.getUploadToastJobIds);
  protected readonly expanded = this.store.selectSignal(DmsIntegrationStateSelectors.isUploadToastExpanded);

  /** "Completed" = every tracked upload finished (with or without errors). */
  protected readonly isCompleted = computed(() => this.summary().phase !== 'in-progress');
  protected readonly hasError = computed(() => this.summary().erroredCount > 0);

  protected readonly headerIcon = computed(() => {
    const { phase } = this.summary();
    if (phase === 'in-progress') return 'upload-cloud';
    if (phase === 'completed-with-errors') return 'alert-circle';
    return 'check-circle';
  });

  protected readonly title = computed(() => {
    const { phase, totalCount, completedCount, erroredCount } = this.summary();
    if (phase === 'in-progress') return `Uploading ${totalCount} ${this.fileWord(totalCount)}`;
    return erroredCount > 0
      ? `${completedCount} of ${totalCount} ${this.fileWord(totalCount)} uploaded`
      : `${totalCount} ${this.fileWord(totalCount)} uploaded`;
  });

  protected readonly subtitle = computed(() => {
    const { phase, totalCount, completedCount, erroredCount } = this.summary();
    if (phase === 'in-progress') {
      const progress = `${completedCount} of ${totalCount} complete`;
      return erroredCount > 0 ? `${erroredCount} ${this.errorWord(erroredCount)} · ${progress}` : progress;
    }
    return erroredCount > 0 ? `${erroredCount} ${this.errorWord(erroredCount)}` : null;
  });

  toggleExpanded(): void {
    this.store.dispatch(new SetUploadToastExpanded(!this.expanded()));
  }

  cancelAll(): void {
    this.store.dispatch(new CancelAllFileUploadJobs());
  }

  dismiss(): void {
    this.store.dispatch(new ResetUploadToast());
  }

  private fileWord(count: number): string {
    return count === 1 ? 'file' : 'files';
  }

  private errorWord(count: number): string {
    return count === 1 ? 'error' : 'errors';
  }
}
